import { useState, useEffect, useRef, useCallback } from "react";

/**
 * =============================================================================
 * useTextScale Hook - Dynamic Text Scaling for Fixed-Height Containers
 * =============================================================================
 *
 * PURPOSE:
 * This hook dynamically scales text sizes to fit content within a fixed-height
 * container. It ensures text remains readable (within min/max bounds) while
 * adapting to varying amounts of content.
 *
 * =============================================================================
 * HOW IT WORKS:
 * =============================================================================
 *
 * 1. BASELINE MEASUREMENT (Two-Phase Approach)
 *    - On initial render or when data changes, scale is reset to 1.0
 *    - The hook measures the "natural" content height at scale=1 (baseline)
 *    - This baseline is stored and used for all subsequent calculations
 *    - This avoids circular dependency issues where measuring scaled content
 *      would give incorrect results
 *
 * 2. SCALE CALCULATION
 *    - Formula: scale = availableHeight / baselineHeight
 *    - availableHeight = container.clientHeight - padding
 *    - Scale is clamped between minScale (0.857) and maxScale (1.143)
 *    - These bounds ensure text stays readable (12px-16px for list items)
 *
 * 3. CSS VARIABLES OUTPUT
 *    - The hook outputs CSS custom properties that can be applied to elements
 *    - Base sizes are multiplied by the scale factor:
 *      - --title-size: 20px * scale (17px to 23px range)
 *      - --section-title-size: 16px * scale (14px to 18px range)
 *      - --item-size: 14px * scale (12px to 16px range)
 *      - --icon-size: 14px * scale (12px to 16px range)
 *
 * 4. RESPONSIVE BEHAVIOR
 *    - ResizeObserver watches for container size changes
 *    - Recalculates scale when container resizes (debounced)
 *    - Recalculates when dependencies change (e.g., data loads)
 *
 * =============================================================================
 * USAGE IN PERFORMANCEINSIGHTSCARD:
 * =============================================================================
 *
 * The PerformanceInsightsCard is a pinned card in the RCC dashboard that displays
 * insights in two categories: "Needs Attention" and "What's Going Well". The card
 * has a fixed height determined by CSS Grid (153px per row × rowSpan).
 *
 * PROBLEM SOLVED:
 * - Content varies: sometimes 2 items, sometimes 10+ items
 * - Fixed card height means content can overflow or leave empty space
 * - Need text to scale down when there's lots of content, scale up when sparse
 *
 * IMPLEMENTATION:
 *
 * ```jsx
 * // In PerformanceInsightsCard.jsx
 *
 * // 1. Initialize the hook with data as dependency
 * const { containerRef, contentRef, cssVars } = useTextScale({
 *   dependencies: [data, loading],  // Recalculate when data changes
 * });
 *
 * // 2. Apply refs and CSS variables to the card structure
 * <Box className={classes.card} ref={containerRef} style={cssVars}>
 *   <Box className={classes.contentRoot} ref={contentRef}>
 *     ... card content ...
 *   </Box>
 * </Box>
 *
 * // 3. Use CSS variables in Typography styles
 * <Typography style={{ fontSize: "var(--title-size, 20px)" }}>
 *   Performance Insights
 * </Typography>
 *
 * <Typography style={{ fontSize: "var(--section-title-size, 16px)" }}>
 *   Needs Attention
 * </Typography>
 *
 * <Typography style={{ fontSize: "var(--item-size, 14px)" }}>
 *   {insight.description}
 * </Typography>
 * ```
 *
 * SPACING STRATEGY:
 * - Text scaling handles font sizes (this hook)
 * - Spacing is handled separately by CSS flexbox with `justify-content: space-evenly`
 * - This combination ensures content fills the container naturally:
 *   - Text stays readable (12-16px bounds)
 *   - Spacing adapts to fill remaining space
 *
 * CSS STRUCTURE IN PERFORMANCEINSIGHTSCARD:
 * ```
 * card (containerRef)
 * └── contentRoot (contentRef, flex, space-evenly)
 *     ├── titleContainer (Performance Insights title)
 *     ├── section (flex: 1, space-evenly)
 *     │   ├── sectionTitle (Needs Attention)
 *     │   └── insightList (flex, space-evenly)
 *     │       ├── insightItem
 *     │       └── insightItem ...
 *     └── section (flex: 1, space-evenly)
 *         ├── sectionTitle (What's Going Well)
 *         └── insightList (flex, space-evenly)
 *             ├── insightItem
 *             └── insightItem ...
 * ```
 *
 * =============================================================================
 * SCALE BOUNDS EXPLAINED:
 * =============================================================================
 *
 * minScale = 0.857 (12/14)
 * - At minimum scale, list items are 14px * 0.857 = 12px
 * - This is the smallest readable size for body text
 *
 * maxScale = 1.143 (16/14)
 * - At maximum scale, list items are 14px * 1.143 = 16px
 * - This prevents text from becoming too large when content is sparse
 *
 * TEXT SIZE RANGES:
 * | Element        | Base | Min (0.857x) | Max (1.143x) |
 * |----------------|------|--------------|--------------|
 * | Card title     | 20px | 17px         | 23px         |
 * | Section titles | 16px | 14px         | 18px         |
 * | List items     | 14px | 12px         | 16px         |
 * | Icons          | 14px | 12px         | 16px         |
 *
 * =============================================================================
 *
 * @param {Object} options - Configuration options
 * @param {number} [options.debounceMs=100] - Debounce delay for resize events (ms)
 * @param {number} [options.padding=32] - Container padding to subtract (px)
 * @param {Array} [options.dependencies=[]] - Dependencies that trigger recalculation
 *
 * @returns {Object} Hook return value
 * @returns {React.RefObject} returns.containerRef - Ref for the outer container
 * @returns {React.RefObject} returns.contentRef - Ref for the content wrapper
 * @returns {number} returns.scale - Current scale factor (0.857 to 1.143)
 * @returns {Object} returns.cssVars - CSS custom properties object to spread on container
 *
 * @example
 * const { containerRef, contentRef, cssVars } = useTextScale({
 *   dependencies: [data],
 *   padding: 32,
 * });
 *
 * return (
 *   <div ref={containerRef} style={cssVars}>
 *     <div ref={contentRef}>
 *       <p style={{ fontSize: "var(--item-size, 14px)" }}>Content</p>
 *     </div>
 *   </div>
 * );
 */
export function useTextScale(options = {}) {
  const {
    debounceMs = 100,
    padding = 32,
    dependencies = [],
  } = options;

  // Refs for DOM elements
  const containerRef = useRef(null); // Outer container with fixed height
  const contentRef = useRef(null); // Inner content to measure
  const baselineHeight = useRef(0); // Content height at scale=1
  const debounceTimer = useRef(null); // Timer for debouncing

  // Scale state (0.857 to 1.143)
  const [scale, setScale] = useState(1);

  /**
   * Calculate the appropriate scale factor based on container and content sizes.
   * Uses the baseline height (content at scale=1) to avoid circular dependencies.
   */
  const calculateScale = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;

    if (!container || !content) return;

    // Get available height (container minus padding)
    const containerHeight = container.clientHeight - padding;

    // If we don't have baseline yet, measure at current scale=1
    if (baselineHeight.current === 0) {
      baselineHeight.current = content.scrollHeight;
    }

    // Avoid division by zero
    if (baselineHeight.current === 0) {
      setScale(1);
      return;
    }

    // Calculate scale based on baseline
    const rawScale = containerHeight / baselineHeight.current;

    // Clamp scale to keep text within readable bounds
    // At scale 1.0: list items = 14px, titles = 16px, card title = 20px
    // Min scale 0.857 (12/14): list items = 12px min
    // Max scale 1.143 (16/14): list items = 16px max
    const minScale = 0.857;
    const maxScale = 1.143;
    const clampedScale = Math.max(minScale, Math.min(maxScale, rawScale));

    setScale(clampedScale);
  }, [padding]);

  /**
   * Debounced version of calculateScale to prevent excessive recalculations
   * during rapid resize events.
   */
  const debouncedCalculate = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(calculateScale, debounceMs);
  }, [calculateScale, debounceMs]);

  /**
   * Set up ResizeObserver to watch for container size changes.
   * This handles browser resize, layout changes, etc.
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      debouncedCalculate();
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [debouncedCalculate]);

  /**
   * Reset baseline and recalculate when dependencies change.
   * This is crucial for handling async data loads - when new data arrives,
   * we need to re-measure the baseline at scale=1.
   */
  useEffect(() => {
    // Reset baseline to force re-measurement
    baselineHeight.current = 0;
    setScale(1); // Reset to scale=1 to measure baseline

    // After DOM updates with scale=1, measure and calculate
    const timer = setTimeout(() => {
      calculateScale();
    }, 50);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies]);

  /**
   * Generate CSS custom properties with fixed px values scaled.
   * These can be spread onto a container element's style prop.
   */
  const cssVars = {
    "--text-scale": scale,
    "--title-size": `${Math.round(20 * scale)}px`,
    "--section-title-size": `${Math.round(16 * scale)}px`,
    "--item-size": `${Math.round(14 * scale)}px`,
    "--icon-size": `${Math.round(14 * scale)}px`,
  };

  return {
    containerRef,
    contentRef,
    scale,
    cssVars,
  };
}

export default useTextScale;
